/**
 * LogLevelControl.js
 *
 * Superuser-only panel to view and change logging verbosity at runtime (no restart).
 * Talks to GET/POST /api/chatbot/admin/log-level. Changes propagate to all workers
 * (server fans out via Redis) and reset on restart — set LOG_LEVEL / VERBOSE_LOG in the
 * environment for a persistent default.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertCircle, Loader2, Bug } from 'lucide-react';
import { apiCall, apiCallJson } from './Api';

const LEVELS = ['ERROR', 'WARNING', 'INFO', 'DEBUG'];
const LOGGER_LEVELS = ['NOTSET', 'DEBUG', 'INFO', 'WARNING', 'ERROR'];

const LogLevelControl = () => {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await apiCallJson('/api/chatbot/admin/log-level');
      setSnap(data);
    } catch (e) {
      setError(e && e.status === 403 ? 'Superuser (ADMIN) access required.' : 'Failed to load log settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const post = useCallback(async (payload) => {
    setBusy(true); setError('');
    try {
      const resp = await apiCall('/api/chatbot/admin/log-level', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        if (resp.status === 403) throw new Error('Superuser (ADMIN) access required.');
        const d = await resp.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      if (data.snapshot) setSnap(data.snapshot);
    } catch (e) {
      setError(e.message || 'Change failed.');
    } finally {
      setBusy(false);
    }
  }, []);

  const root = snap?.root;
  const loggers = snap?.loggers || {};
  // "Verbose on" == the high-volume loggers are effectively at DEBUG.
  const verboseOn = Object.values(loggers).some(l => l.effective === 'DEBUG');

  if (loading) {
    return <div className="p-6 flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }
  if (error && !snap) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 text-red-600 text-sm"><AlertCircle className="w-4 h-4" /> {error}</div>
        <button onClick={load} className="mt-3 text-sm text-blue-600 underline">Retry</button>
      </div>
    );
  }

  return (
    <div className="p-5 space-y-5 overflow-y-auto">
      <p className="text-sm text-gray-600">
        Controls logging verbosity for the running service. Customer‑message handling and
        API‑call detail log only when <span className="font-medium">Verbose</span> is on.
        Changes take effect immediately across all workers and reset on restart (set{' '}
        <span className="font-mono">LOG_LEVEL</span> / <span className="font-mono">VERBOSE_LOG</span>{' '}
        in the environment for a persistent default).
      </p>

      {error && <div className="flex items-center gap-2 text-red-600 text-xs"><AlertCircle className="w-4 h-4" /> {error}</div>}

      {/* Verbose toggle */}
      <div className="flex items-center justify-between border rounded-lg p-3 bg-gray-50">
        <div className="flex items-center gap-2">
          <Bug className="w-4 h-4 text-fuchsia-600" />
          <div>
            <div className="text-sm font-medium text-gray-800">Verbose (debug) logging</div>
            <div className="text-[11px] text-gray-500">Customer messages & API‑call detail. Turn on only while debugging.</div>
          </div>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={() => post({ verbose: !verboseOn })}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${verboseOn ? 'bg-fuchsia-600' : 'bg-gray-300'} ${busy ? 'opacity-60' : ''}`}
          title={verboseOn ? 'Turn verbose off' : 'Turn verbose on'}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${verboseOn ? 'translate-x-5' : 'translate-x-1'}`} />
        </button>
      </div>

      {/* Root level */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-gray-800">Root level</div>
          <div className="text-[11px] text-gray-500">Baseline for everything not overridden below.</div>
        </div>
        <div className="flex gap-1">
          {LEVELS.map(l => (
            <button
              key={l}
              type="button"
              disabled={busy}
              onClick={() => post({ level: l })}
              className={`px-2.5 py-1 rounded text-xs font-medium border ${root === l ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Per-logger overrides */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-sm font-medium text-gray-800">High‑volume loggers</div>
          <button onClick={load} disabled={busy} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
            <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
        <div className="border rounded-lg divide-y">
          {Object.entries(loggers).map(([name, lvl]) => (
            <div key={name} className="flex items-center justify-between px-3 py-1.5">
              <span className="text-xs font-mono text-gray-700">{name}</span>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${lvl.effective === 'DEBUG' ? 'bg-fuchsia-100 text-fuchsia-700' : 'bg-gray-100 text-gray-500'}`}>
                  {lvl.effective}{lvl.set === 'inherit' ? ' (inherit)' : ''}
                </span>
                <select
                  value={lvl.set === 'inherit' ? 'NOTSET' : lvl.set}
                  disabled={busy}
                  onChange={(e) => post({ logger: name, level: e.target.value })}
                  className="border border-gray-300 rounded px-1 py-0.5 text-[11px]"
                >
                  {LOGGER_LEVELS.map(l => <option key={l} value={l}>{l === 'NOTSET' ? 'Inherit' : l}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LogLevelControl;
